// The pager (#15581): the footer under a table read a page at a time, in the RMS grid's form.
// On the left, which rows are showing ("26–50 of 312"); on the right, previous and next carets
// around the numbered page cells (`pageWindow`: always the first and last page, the current one
// filled with ink, gaps as "…"). On a phone the cells stand down for "page 2 of 13" between the
// carets, each a 44px target. One page shows only its count ("1–12 of 12"): there is nowhere to
// go. A set whose total could not be read says how many rows show and pages on while a page comes
// back full. Every control is a button, so the pager is reached and worked from the keyboard.

import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { GAP, pageWindow } from "../Search/Components/pageWindow";

export interface PagerProps {
  /** The page showing, from 1. */
  page: number;
  /** Rows a page holds. */
  pageSize: number;
  /** Rows in the whole set; null when it could not be read. */
  total: number | null;
  /** Rows this page holds, for a set whose total could not be read. */
  shown?: number;
  onPage: (page: number) => void;
  /** What the pages are of, for the control's accessible name ("Total NAV rows"). */
  label: string;
}

/** How many pages a set of `total` rows takes, `pageSize` to a page; never fewer than one. */
export const pageCount = (total: number, pageSize: number): number =>
  Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

/** Which rows a page shows, as the footer says it: "26–50 of 312". */
export const pageRange = (
  page: number,
  pageSize: number,
  total: number
): string => {
  const first = Math.min(total, (page - 1) * pageSize + 1);
  const last = Math.min(total, page * pageSize);
  return `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`;
};

const CARET =
  "inline-flex items-center justify-center rounded-[7px] text-[var(--fp-ink-3)] " +
  "enabled:cursor-pointer enabled:hover:bg-[var(--fp-hover)] disabled:opacity-30 " +
  "disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 " +
  "focus-visible:outline-[var(--fp-ink-3)]";

const CELL =
  "inline-flex h-[30px] w-[30px] items-center justify-center rounded-[7px] " +
  "[font-family:var(--fp-font-num)] text-[length:var(--fp-t-meta)] tabular-nums " +
  "[font-weight:var(--fp-w-strong)] focus-visible:outline focus-visible:outline-2 " +
  "focus-visible:outline-[var(--fp-ink-3)]";

/** What the footer says of a page when the set's total could not be read. */
export const unknownTotalRange = (shown: number): string =>
  `${shown.toLocaleString()} ${shown === 1 ? "row" : "rows"} shown; the total could not be read`;

export function Pager({
  page,
  pageSize,
  total,
  shown = 0,
  onPage,
  label,
}: PagerProps) {
  const phone = useIsPhoneViewport();
  // With no total, the pages run on while a page comes back full.
  const pages = total === null ? null : pageCount(total, pageSize);
  const current = Math.max(1, pages === null ? page : Math.min(page, pages));
  const hasNext = pages === null ? shown >= pageSize : current < pages;
  const range = (
    <span
      className="whitespace-nowrap text-[length:var(--fp-t-meta)] text-[var(--fp-ink-3)] tabular-nums"
      role="status"
    >
      {total === null
        ? unknownTotalRange(shown)
        : pageRange(current, pageSize, total)}
    </span>
  );
  // One page: the count still shows, with nowhere to go.
  if (current === 1 && !hasNext)
    return total === 0 ? null : <div className="fp-pager">{range}</div>;
  const caret = (direction: "previous" | "next") => {
    const to = direction === "previous" ? current - 1 : current + 1;
    const Icon = direction === "previous" ? ChevronLeftIcon : ChevronRightIcon;
    return (
      <button
        type="button"
        className={`${CARET} ${phone ? "h-11 w-11" : "h-[30px] w-[30px]"}`}
        aria-label={direction === "previous" ? "Previous page" : "Next page"}
        disabled={direction === "previous" ? to < 1 : !hasNext}
        onClick={() => onPage(to)}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
    );
  };
  return (
    <nav
      className="fp-pager flex flex-wrap items-center justify-between gap-x-3 gap-y-1"
      aria-label={label}
      data-phone-pager={phone ? "" : undefined}
    >
      {range}
      <span className="flex items-center gap-1">
        {caret("previous")}
        {phone || pages === null ? (
          <span className="whitespace-nowrap text-[length:var(--fp-t-meta)] text-[var(--fp-ink-3)] tabular-nums">
            {pages === null
              ? `page ${current.toLocaleString()}`
              : `page ${current.toLocaleString()} of ${pages.toLocaleString()}`}
          </span>
        ) : (
          pageWindow(current, pages).map((slot, index) =>
            slot === GAP ? (
              <span
                key={`gap-${index}`}
                aria-hidden="true"
                className="w-5 select-none text-center text-[var(--fp-ink-4)]"
              >
                …
              </span>
            ) : (
              <button
                key={slot}
                type="button"
                className={`${CELL} ${
                  slot === current
                    ? "bg-[var(--fp-ink)] text-[var(--fp-surface)]"
                    : "cursor-pointer text-[var(--fp-ink-3)] hover:bg-[var(--fp-hover)]"
                }`}
                aria-label={`Page ${slot}`}
                aria-current={slot === current ? "page" : undefined}
                onClick={() => slot !== current && onPage(slot)}
              >
                {slot}
              </button>
            )
          )
        )}
        {caret("next")}
      </span>
    </nav>
  );
}
